import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CardElement } from '@stripe/react-stripe-js';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import { InputField } from 'shared/forms';
import { useTranslation } from 'hooks/useTranslation';
import useValidation from 'hooks/useValidation';

import BaseButton from 'features/app/components/BaseButton';
import { TopUpSummary } from 'features/finance/components/TopUpSummary';
import { useTopUpBalanceByCreditCardSummary } from 'features/finance/hooks/useTopUpBalanceByCreditCardSummary';
import { getFormattedAmountWithCurrency } from 'features/finance/utils/get-formatted-amount-with-currency';

import { useStyles } from './styles';

type Props = {
  onPreviousStep: () => void;
  onFormSubmit: () => void;
  isPaying: boolean;
};

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: '16px',
      color: '#2D2D2D',
      '::placeholder': { color: '#ABAFB3' },
    },
    invalid: { color: '#d32f2f' },
  },
};

export const TopUpBalanceCardDetailsPart: React.FC<Props> = ({ onPreviousStep, onFormSubmit, isPaying }) => {
  const classes = useStyles();
  const { t } = useTranslation('finance');
  const { required } = useValidation();
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState('');

  const {
    watch,
    formState: { isSubmitting },
    trigger,
  } = useFormContext() ?? {};

  const currency = watch('currency');
  const amount = watch('amount');

  const { data, isLoading } = useTopUpBalanceByCreditCardSummary(amount, currency?.code);

  const handleFormSubmit = async () => {
    const isFormValid = await trigger(['card_holder_name']);
    if (!isFormValid || !cardComplete || isPaying) return;

    onFormSubmit();
  };

  const payButtonText = useMemo(() => {
    let text = t('topUpBalanceForm.pay');
    const amount = data?.total_amount,
      currency = data?.currency;

    if (amount && currency) text += ' ' + getFormattedAmountWithCurrency(amount, currency);

    return text;
  }, [t, data]);

  return (
    <>
      <Box marginTop={25} marginBottom={11}>
        <Typography variant="h4" className={classes.cardDetailsTitle}>
          {t('topUpBalanceForm.cardDetails')}
        </Typography>
      </Box>
      <InputField
        name="card_holder_name"
        fullWidth
        placeholder={t('inputs.name', { ns: 'common' })}
        label={t('topUpBalanceForm.cardHolderName')}
        required
        type="text"
        rules={{
          validate: {
            ...required,
          },
        }}
        className={classes.topUpInputField}
      />
      <Typography className={classes.cardElementLabel}>{t('topUpBalanceForm.cardNumber')}</Typography>
      <Box className={classes.cardElement}>
        <CardElement
          options={CARD_ELEMENT_OPTIONS}
          onChange={event => {
            setCardComplete(event.complete);
            setCardError(event.error?.message ?? '');
          }}
        />
      </Box>
      {cardError ? (
        <Typography color="error" className={classes.cardError}>
          {cardError}
        </Typography>
      ) : null}

      {currency && amount ? <TopUpSummary currency={currency?.code} amount={amount} /> : null}

      <Box display="flex" justifyContent="space-evenly" alignItems="center" paddingTop={19}>
        <BaseButton
          fullWidth
          maxWidth={219}
          variant="outlined"
          onClick={onPreviousStep}
          disabled={isPaying}
          className={classes.topFormButton}
          labelClass={classes.topFormButtonLabel}
        >
          {t('back', { ns: 'common' })}
        </BaseButton>
        <BaseButton
          fullWidth={false}
          isLoading={isSubmitting || isLoading || isPaying}
          disabled={isSubmitting || isLoading || isPaying || !cardComplete}
          onClick={handleFormSubmit}
          className={classes.topFormButton}
          labelClass={classes.topFormButtonLabel}
        >
          {payButtonText}
        </BaseButton>
      </Box>
    </>
  );
};
